import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ProgressSteps from '../components/ToolBox/ProgressSteps';
import Step1_UrlInput from '../components/ToolBox/DoctorScheduleGenerator/Step1_UrlInput';
import Step2_Scraping from '../components/ToolBox/DoctorScheduleGenerator/Step2_Scraping';
import Step3_Preview from '../components/ToolBox/DoctorScheduleGenerator/Step3_Preview';
import Step4_Complete from '../components/ToolBox/DoctorScheduleGenerator/Step4_Complete';
import { useToolBox } from '../contexts/ToolBoxContext';

const DoctorScheduleGenerator = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { doctorScheduleState, updateDoctorScheduleState, clearDoctorScheduleState } = useToolBox();

  // Initialize state from context or URL params (lazy initialization)
  const [currentStep, setCurrentStep] = useState(() => doctorScheduleState?.currentStep || 0);
  const [urls, setUrls] = useState(() => doctorScheduleState?.urls || []);
  const [clid, setClid] = useState(() => doctorScheduleState?.clid || searchParams.get('clid') || '');
  const [scrapingResults, setScrapingResults] = useState(() => doctorScheduleState?.scrapingResults || []);
  const [importSummary, setImportSummary] = useState(() => doctorScheduleState?.importSummary || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Update state from context when it becomes available (non-blocking)
  useEffect(() => {
    if (doctorScheduleState && (currentStep === 0 && urls.length === 0)) {
      // Only update if we don't have local state yet
      setCurrentStep(doctorScheduleState.currentStep || 0);
      setUrls(doctorScheduleState.urls || []);
      setClid(doctorScheduleState.clid || searchParams.get('clid') || '');
      setScrapingResults(doctorScheduleState.scrapingResults || []);
      setImportSummary(doctorScheduleState.importSummary || null);
    }
  }, [doctorScheduleState, currentStep, urls.length, searchParams]);

  // Save state to context with debouncing to avoid blocking
  useEffect(() => {
    // Debounce state updates to avoid blocking
    const timeoutId = setTimeout(() => {
      updateDoctorScheduleState({
        currentStep,
        urls,
        clid,
        scrapingResults,
        importSummary,
      });
    }, 200); // 200ms debounce

    return () => clearTimeout(timeoutId);
  }, [currentStep, urls, clid, scrapingResults, importSummary, updateDoctorScheduleState]);

  const handleStep1Next = () => {
    if (urls.length === 0) {
      setError('Please add at least one URL');
      return;
    }
    setError('');
    setCurrentStep(1);
  };

  const handleStep2Complete = (results) => {
    setScrapingResults(results);
    setCurrentStep(2);
  };

  const handleStep3Complete = (summary) => {
    if (summary?.success) {
      setLoading(false);
      setImportSummary(summary);
      if (summary.clid) {
        setClid(summary.clid);
      }
      setCurrentStep(3);
    }
  };

  const handleRestartFlow = () => {
    clearDoctorScheduleState();
    setCurrentStep(0);
    setUrls([]);
    setClid(searchParams.get('clid') || '');
    setScrapingResults([]);
    setImportSummary(null);
    setError('');
  };

  // Clear state function (can be called manually if needed)
  const handleClearState = () => {
    if (window.confirm('Are you sure you want to clear all progress? This cannot be undone.')) {
      clearDoctorScheduleState();
      setCurrentStep(0);
      setUrls([]);
      setClid('');
      setScrapingResults([]);
      setImportSummary(null);
      setError('');
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
    setError('');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <div className="text-center mb-8">
          <div className="flex justify-between items-center mb-2">
            <div className="flex-1"></div>
            <h1 className="text-3xl font-bold text-gray-900 flex-1">Doctor Schedule Generator</h1>
            <div className="flex-1 flex justify-end">
              {(currentStep > 0 || urls.length > 0) && (
                <button
                  onClick={handleClearState}
                  className="text-sm text-red-600 hover:text-red-700 px-3 py-1 border border-red-300 rounded hover:bg-red-50"
                  title="Clear all progress"
                >
                  Clear Progress
                </button>
              )}
            </div>
          </div>
          <p className="text-gray-600">Extract doctor information and schedules from web pages</p>
          {doctorScheduleState && (currentStep > 0 || urls.length > 0) && (
            <p className="text-sm text-blue-600 mt-2">
              💾 Your progress is saved. You can navigate away and return anytime.
            </p>
          )}
        </div>

        <ProgressSteps currentStep={currentStep} totalSteps={4} />

        {/* Step 1: URL Input */}
        {currentStep === 0 && (
          <Step1_UrlInput
            urls={urls}
            setUrls={setUrls}
            onNext={handleStep1Next}
            loading={loading}
            error={error}
          />
        )}

        {/* Step 2: Scraping */}
        {currentStep === 1 && (
          <Step2_Scraping
            urls={urls}
            clid={clid}
            onComplete={handleStep2Complete}
            onBack={() => goToStep(0)}
            loading={loading}
            error={error}
          />
        )}

        {/* Step 3: Preview */}
        {currentStep === 2 && (
          <Step3_Preview
            results={scrapingResults}
            clid={clid}
            onBack={() => goToStep(1)}
            onComplete={handleStep3Complete}
            loading={loading}
            error={error}
            setLoading={setLoading}
            setError={setError}
          />
        )}

        {currentStep === 3 && (
          <Step4_Complete
            summary={importSummary}
            clid={clid}
            onRestart={handleRestartFlow}
          />
        )}
      </div>
    </div>
  );
};

export default DoctorScheduleGenerator;

